import { Component, OnInit, OnDestroy } from '@angular/core';
import { DataParametersResponse, BaseDataset, AnalysisContent } from '../../../../shared/templateGen/templateInterfaces';
import { StatoSessioneService } from '../../../../shared/services/stato-sessione.service';
import { Subject } from 'rxjs';
import { catchError, take, takeUntil } from 'rxjs/operators';
import { UserStoryService } from '../../../../shared/templateGen/template-api.service';

@Component({
  selector: 'app-data-param-table',
  templateUrl: './data-param-table.component.html',
  styleUrls: ['./data-param-table.component.scss']
})
export class DataParamTableComponent implements OnInit, OnDestroy {
  dataParameters?: DataParametersResponse;
  private destroy$ = new Subject<void>();

  isGenerating = false;
  generationError: string | null = null;
  isCardVisible = false;

  // --- State for Inline Editing ---
  editingRowIndex: number | null = null;
  isAddingNew = false;
  private originalRowData: BaseDataset | null = null;
  newRowData: BaseDataset = this.createEmptyRow();

  // Definitions for Tooltips
  readonly TYPE_DEFINITIONS = {
    CTX: "Selects the process/scenario context.",
    INP: "Entity attributes or controllable inputs."
  };

  constructor(
    private sessioneService: StatoSessioneService,
    private templateApiService: UserStoryService,
  ) {}

  ngOnInit(): void {
    this.sessioneService.dataParameters$.pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Error:', error);
        this.dataParameters = undefined;
        this.cancelEdit();
        return [];
      })
    ).subscribe(dataParams => {
      if (this.editingRowIndex !== null || this.isAddingNew) {
        this.cancelEdit();
      }
      this.dataParameters = dataParams;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- CRUD Methods (Unchanged Logic) ---
  private createEmptyRow(): BaseDataset {
    return { field: '', valid_values: [], invalid_values: [], rules: '' };
  }

  onEdit(item: BaseDataset, index: number): void {
    this.cancelEdit();
    this.editingRowIndex = index;
    this.originalRowData = JSON.parse(JSON.stringify(item));
  }

  onSave(index: number): void {
    if (this.editingRowIndex === index && this.dataParameters) {
      this.sessioneService.updateDataParameters(this.dataParameters);
      this.editingRowIndex = null;
      this.originalRowData = null;
    }
  }

  cancelEdit(): void {
    if (this.editingRowIndex !== null && this.originalRowData && this.dataParameters?.analysis_results?.test_data_management?.base_dataset) {
      this.dataParameters.analysis_results.test_data_management.base_dataset[this.editingRowIndex] = this.originalRowData;
    }
    this.editingRowIndex = null;
    this.originalRowData = null;
    this.isAddingNew = false;
    this.newRowData = this.createEmptyRow();
  }

  onDelete(index: number): void {
    if (confirm('Delete this parameter?')) {
      this.dataParameters?.analysis_results?.test_data_management?.base_dataset.splice(index, 1);
      this.sessioneService.updateDataParameters(this.dataParameters);
      if (this.editingRowIndex === index) this.cancelEdit();
    }
  }

  onAddNew(): void {
    this.cancelEdit();
    this.isAddingNew = true;
  }

  onSaveNew(): void {
    if (!this.newRowData.field.trim()) return;
    if (!this.dataParameters) return;
    if (!this.dataParameters.analysis_results.test_data_management.base_dataset)
      this.dataParameters.analysis_results.test_data_management.base_dataset = [];

    this.dataParameters.analysis_results.test_data_management.base_dataset.push(this.newRowData);
    this.sessioneService.updateDataParameters(this.dataParameters);
    this.isAddingNew = false;
    this.newRowData = this.createEmptyRow();
  }

  get baseDatasetItems(): BaseDataset[] {
    return this.dataParameters?.analysis_results?.test_data_management?.base_dataset || [];
  }

  updateArrayValue(item: BaseDataset, fieldKey: 'valid_values' | 'invalid_values', val: string): void {
    item[fieldKey] = val.split(',').map(s => s.trim()).filter(Boolean);
  }

  generateTableData(): void {
    this.isGenerating = true;
    this.generationError = null;
    this.cancelEdit();

    // ... (Logica di generazione API esistente mantenuta identica) ...
    // Simuliamo la fine per brevità codice risposta
    const projectId = this.sessioneService.projectId.getValue();
    const nome_sessione = this.sessioneService.getSessionName();
    const story = this.sessioneService.getUserStory() ?? '';
    const language = this.sessioneService.getSelectedLanguage ?? 'en';
    const core = this.sessioneService.getCoreAnalysis()?.scenario_analysis ?? '';
    const reqs = this.sessioneService.getRequirementsAnalysis()?.scenario_analysis ?? '';
    const supportive = this.sessioneService.getSupportiveAnalysis()?.scenario_enrichments ?? '';

    this.templateApiService.generateDataParameters(projectId, nome_sessione, story, language, `${core}\n\n${reqs}`, supportive)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.sessioneService.updateDataParameters(res);
          // Update analysis context if needed logic here
          this.isGenerating = false;
        },
        error: (err) => {
          this.generationError = err.message;
          this.isGenerating = false;
        }
      });
  }

  // ==========================================
  // VIEW PARSING HELPERS (UPDATED)
  // ==========================================

  /** 1. Extracts Type and Name. Returns clean name and type code. */
  parseField(rawField: string): { type: 'CTX' | 'INP' | 'PARAM', name: string } {
    if (!rawField) return { type: 'PARAM', name: '' };
    const match = rawField.match(/^\[(CTX|INP)\]\s*(.*)/i);
    if (match) {
      return {
        type: match[1].toUpperCase() as 'CTX' | 'INP',
        name: match[2].trim()
      };
    }
    return { type: 'PARAM', name: rawField };
  }

  /**
   * 2. Cleans Value String.
   * Input: "EP: [NONE]" -> Output: "NONE"
   * Input: "BVA: < 1" -> Output: "< 1"
   */
  getCleanValue(val: string): string {
    if (!val) return '';
    // Remove "EP:", "BVA:", "FMT:" (case insensitive) followed by optional spaces
    let clean = val.replace(/^(EP|BVA|FMT|TYPE):\s*/i, '');
    // Remove square brackets [ ]
    clean = clean.replace(/[\[\]]/g, '');
    return clean.trim();
  }

  /**
   * 3. Cleans Rules String.
   * Removes "TYPE: XXX." and returns the rest.
   */
  getCleanRule(rule: string): string {
    if (!rule) return '';
    // Match "TYPE: word" followed by optional dot or punctuation and space
    // Replace with empty string to leave only the purpose
    return rule.replace(/^TYPE:\s*[A-Za-z]+[\.,;]?\s*/i, '').trim();
  }

  /** Returns the tooltip definition based on type */
  getTypeDefinition(type: string): string {
    if (type === 'CTX') return this.TYPE_DEFINITIONS.CTX;
    if (type === 'INP') return this.TYPE_DEFINITIONS.INP;
    return '';
  }
}
